import React from "react";
import {
  Box,
  Card,
  CardContent,
  IconButton,
  Typography,
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { ReactComponent as PdfIcon } from "../../../assets/svg/SmallPdfIcon.svg";

// Pull every contract-like url off the proposal. The proposal can carry
// either a single string (legacy contract_url / reno_contract_url) or
// an array of contract files; we flatten everything into a single list
// so the UI can render them stacked top-to-bottom.
const collectContractFiles = (proposal) => {
  if (!proposal) return [];
  const list = [];

  const push = (entry, fallbackLabel) => {
    if (!entry) return;
    if (typeof entry === "string") {
      list.push({ url: entry, label: fallbackLabel });
    } else if (typeof entry === "object") {
      const url =
        entry.url ||
        entry.image_url ||
        entry.file_url ||
        entry.path ||
        entry.contract_url ||
        null;
      if (!url) return;
      list.push({
        url,
        label: entry.name || entry.title || entry.tag || fallbackLabel,
      });
    }
  };

  // Singular legacy fields
  push(proposal.contract_url, "Contract");
  push(proposal.contract_file, "Contract");
  push(proposal.reno_contract_url, "Reno Contract");
  push(proposal.reno_contract_file, "Reno Contract");

  // Arrayed fields the backend may send.
  const arrays = [
    proposal.contracts,
    proposal.contract_files,
    proposal.contract_documents,
    proposal.documents,
  ];
  arrays.forEach((arr) => {
    if (Array.isArray(arr)) arr.forEach((item) => push(item, "Contract"));
  });

  return list;
};

const fileNameOf = (url, fallback) =>
  (typeof url === "string" && url.split("/").pop()) || fallback;

const PdfTile = ({ url, label }) => {
  const handleOpen = () => {
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };
  const fileName = fileNameOf(url, label);
  return (
    <Box
      onClick={handleOpen}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        padding: "12px 16px",
        border: "1px solid #E5E7EB",
        borderRadius: "10px",
        backgroundColor: "#F9FAFB",
        cursor: "pointer",
        "&:hover": { borderColor: "#4D6BF6" },
      }}
      title={fileName}
    >
      <PdfIcon style={{ width: 32, height: 32, flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 12,
            color: "#9CA3AF",
            fontFamily: "Inter Tight",
            fontWeight: 500,
            mb: 0.25,
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 500,
            color: "#0D0D0E",
            fontFamily: "Inter Tight",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {fileName}
        </Typography>
      </Box>
      <IconButton size="small" title="Preview" sx={{ color: "#4D6BF6" }}>
        <VisibilityOutlinedIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

export default function Contract({ proposal }) {
  const files = collectContractFiles(proposal);

  return (
    <Card
      sx={{
        borderRadius: "12px",
        boxShadow: "none",
        margin: "1vh 0",
        border: "1px solid #E5E7EB",
      }}
    >
      <CardContent sx={{ padding: "20px" }}>
        <Typography
          sx={{
            fontSize: 16,
            fontWeight: 600,
            color: "#0D0D0E",
            mb: 2,
            fontFamily: "Inter Tight",
          }}
        >
          Contracts
        </Typography>

        {files.length === 0 ? (
          <Typography
            sx={{
              fontSize: 14,
              color: "#9CA3AF",
              fontFamily: "Inter Tight",
            }}
          >
            No contract uploaded yet.
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {files.map((f, idx) => (
              <PdfTile key={`${f.url}-${idx}`} url={f.url} label={f.label} />
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
